import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useCart } from "@/context/CartContext";
import { useSite } from "@/context/SiteContext";
import { productById } from "@/data/catalog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const JOINED_KEY = "dp_corr_joined";

export const CorrespondenceForm = ({ id, compact = false, interest = "" }) => {
  const { t, lang } = useLang();
  const { applyCode } = useCart();
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return setError(t("Please tick the box so we may write to you.", "Veuillez cocher la case pour que nous puissions vous écrire."));
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/correspondence/signup`, { email, consent, lang, origin_url: window.location.origin, interest: interest || null });
      localStorage.setItem(JOINED_KEY, "1");
      setResult(data);
      if (!data.redeemed) applyCode(data.code).catch(() => {});
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : t("That address doesn't look quite right.", "Cette adresse ne semble pas tout à fait juste."));
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <div data-testid={`${id}-success`} data-no-translate className={compact ? "" : "text-center"}>
        <p className="font-display italic text-xl text-[#E8DDC7]">
          {result.returning ? t("You're already on the list.", "Vous êtes déjà sur la liste.") : t("You're on the list. We'll write when ordering opens.", "Vous êtes sur la liste. Nous vous écrirons à l'ouverture des commandes.")}
        </p>
        {result.redeemed ? (
          <p className="mt-3 text-[13px] text-[#D3CEC4]/60">{t("Your welcome code has already been used on a first order.", "Votre code de bienvenue a déjà servi sur une première commande.")}</p>
        ) : (
          <>
            <p className="mt-4 text-[11px] uppercase tracking-[0.3em] text-[#E8DDC7]/75">{t("Your free-shipping code", "Votre code de livraison gratuite")}</p>
            <p data-testid={`${id}-code`} className="mt-2 inline-block font-ledger text-2xl tracking-[0.3em] text-[#B5A642] border border-dashed border-[#B5A642]/60 px-5 py-2.5">{result.code}</p>
            <p className="mt-3 text-[13px] text-[#E8DDC7]/75">
              {t("Already saved in your cart. One use, on your first order, alongside any multi-jar savings.", "Déjà enregistré dans votre panier. Usage unique, sur votre première commande, cumulable avec les rabais multi-pots.")}
            </p>
          </>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={`${id}-form`} data-no-translate>
      <div className={`flex gap-2 ${compact ? "flex-col sm:flex-row" : "flex-col"}`}>
        <input
          type="email"
          required
          data-testid={`${id}-email`}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("Your email address", "Votre adresse courriel")}
          className="flex-1 min-w-0 bg-[#111A14]/60 border border-[#303B2D] rounded-sm px-4 py-3 text-[14px] text-[#E8DDC7] placeholder:text-[#D3CEC4]/35 focus:outline-none focus:border-[#B5A642]/70 transition-colors"
        />
        <button
          data-testid={`${id}-submit`}
          disabled={busy}
          className="btn-primary"
        >
          {busy ? t("Sending…", "Envoi…") : t("Join the list", "Rejoindre la liste")}
        </button>
      </div>
      <label className="mt-3 flex items-start gap-2.5 cursor-pointer text-left">
        <input
          type="checkbox"
          data-testid={`${id}-consent`}
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 accent-[#B5A642] w-3.5 h-3.5 shrink-0"
        />
        <span className="text-[12px] leading-relaxed text-[#E8DDC7]/75">
          {t(
            "Yes, email me when ordering opens, plus new batches and the occasional offer. Unsubscribe any time.",
            "Oui, écrivez-moi à l'ouverture des commandes, ainsi que pour les nouveaux lots et les offres occasionnelles. Désabonnement en tout temps."
          )}
        </span>
      </label>
      {error && <p data-testid={`${id}-error`} className="mt-2 text-[12px] text-[#C9876B]">{error}</p>}
    </form>
  );
};

const OfferCopy = ({ t }) => (
  <>
    <p className="eyebrow" data-no-translate>{t("Ordering opens soon", "Les commandes ouvrent bientôt")}</p>
    <p className="mt-3 font-display text-2xl md:text-3xl text-[#E8DDC7] leading-snug" data-no-translate>
      {t("Join the list for free shipping on your first order.", "Inscrivez-vous pour la livraison gratuite sur votre première commande.")}
    </p>
  </>
);

/* The one sign-up offer on the site. Opened by "Notify me" and the top strip. */
export const SignupDialog = () => {
  const { t } = useLang();
  const { signup, closeSignup } = useSite();
  const jar = signup?.interest ? productById(signup.interest) : null;
  return (
    <AnimatePresence>
      {signup && (
        <motion.div key="signup" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-[#0E0F10]/80 backdrop-blur-sm flex items-center justify-center px-4" onClick={closeSignup} data-testid="signup-dialog-backdrop">
          <div role="dialog" aria-modal="true" aria-label={t("Join the list", "Rejoindre la liste")} data-testid="signup-dialog" onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md max-h-[92vh] overflow-y-auto bg-[#16181A] border border-[#3F6B48]/60 rounded-sm px-7 py-9">
            <button data-testid="signup-dialog-close" onClick={closeSignup} aria-label={t("Close", "Fermer")}
              className="absolute top-3 right-3 p-2 text-[#E8DDC7]/70 hover:text-[#E8DDC7] transition-colors">
              <X className="w-4 h-4" />
            </button>
            <OfferCopy t={t} />
            {jar && <p className="mt-3 text-[14px] text-[#E8DDC7]/80" data-testid="signup-dialog-jar">{t(`We'll note your interest in ${jar.name}.`, `Nous noterons votre intérêt pour ${jar.name}.`)}</p>}
            <div className="mt-6"><CorrespondenceForm id="signup-dialog" interest={signup.interest} /></div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const CorrespondenceStrip = () => {
  const { t } = useLang();
  return (
    <div id="join" data-testid="correspondence-strip" className="join">
      <div data-no-translate>
        <div className="eyebrow">{t("Ordering opens soon", "Les commandes ouvrent bientôt")}</div>
        <p className="offer">{t("Join the list for free shipping on your first order.", "Inscrivez-vous pour la livraison gratuite sur votre première commande.")}</p>
      </div>
      <CorrespondenceForm id="correspondence-strip" compact />
    </div>
  );
};
